// Developer tool: the agents workflows run. The global library plus per-tenant
// copies; each agent is instructions + model + tools (data) and an output
// contract (code) that decides what it hands back to the workflow.

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, History, MessageSquareText, Pencil, Plus, Trash2, X } from "lucide-mui";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  api,
  getErrorMessage,
  type AgentContractKey,
  type AgentDefinition,
  type AgentDefinitionCreate,
  type AgentDefinitionUpdate,
  type AgentOutputField,
  type AgentOutputFieldType,
  type DataSourceOption,
  type Tenant,
} from "../../../api/client.ts";
import { useAgentDataSources, useTenants } from "../../../hooks/resources.ts";
import { keys } from "../../../api/queryKeys.ts";
import AdvancedModelFold from "../../../components/agent/AdvancedModelFold.tsx";
import RowActions from "../../../components/RowActions.tsx";
import ScopeChip from "../../../components/ScopeChip.tsx";
import FilterBar from "../../../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../../../components/filters/specs.ts";
import { useFilters, useLocalFilterState } from "../../../components/filters/useFilters.ts";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import { formatNumber } from "../../../utils/format.ts";
import AgentFeedbackDialog from "./AgentFeedbackDialog.tsx";
import { CONTRACT_LABELS } from "../../workflows/editor/types.ts";
import PageHeader from "../../../components/PageHeader.tsx";

const GLOBAL = "__global__";
const FIELD_TYPES: AgentOutputFieldType[] = ["string", "number", "integer", "boolean", "enum"];
const NO_TENANTS: Tenant[] = [];
const NO_SOURCES: DataSourceOption[] = [];

interface Draft {
  id: string | null;
  builtin_key: string | null;
  workflow_count: number;
  tenant: string; // GLOBAL or a tenant id; fixed after creation
  name: string;
  description: string;
  instructions: string;
  is_default_instructions: boolean;
  reset_instructions: boolean;
  model: string;
  max_turns: number;
  hive_tools: string[];
  // Non-implicit data sources a workflow step may opt in to (hive is always on).
  data_sources: string[];
  web_access: boolean;
  enabled: boolean;
  output_contract: AgentContractKey;
  output_fields: AgentOutputField[];
}

function newDraft(tenant: string): Draft {
  return {
    id: null,
    builtin_key: null,
    workflow_count: 0,
    tenant,
    name: "",
    description: "",
    instructions: "",
    is_default_instructions: false,
    reset_instructions: false,
    model: "",
    max_turns: 100,
    hive_tools: ["search_companies", "get_company", "get_filter_options"],
    data_sources: [],
    web_access: true,
    enabled: true,
    output_contract: "text",
    output_fields: [],
  };
}

function draftOf(agent: AgentDefinition): Draft {
  return {
    id: agent.id,
    builtin_key: agent.builtin_key,
    workflow_count: agent.workflow_count,
    tenant: agent.tenant_id ?? GLOBAL,
    name: agent.name,
    description: agent.description ?? "",
    instructions: agent.instructions,
    is_default_instructions: agent.is_default_instructions,
    reset_instructions: false,
    model: agent.model ?? "",
    max_turns: agent.max_turns,
    hive_tools: agent.hive_tools,
    data_sources: agent.data_sources ?? [],
    web_access: agent.web_access,
    enabled: agent.enabled,
    output_contract: agent.output_contract,
    output_fields: agent.output_fields,
  };
}

function bodyOf(d: Draft): AgentDefinitionCreate {
  return {
    tenant_id: d.tenant === GLOBAL ? null : d.tenant,
    name: d.name.trim(),
    description: d.description.trim() || null,
    instructions: d.instructions,
    model: d.model.trim() || null,
    max_turns: d.max_turns,
    hive_tools: d.hive_tools,
    data_sources: d.data_sources,
    web_access: d.web_access,
    enabled: d.enabled,
    output_contract: d.output_contract,
    output_fields: d.output_contract === "structured" ? d.output_fields : [],
  };
}

function OutputFieldsEditor({
  fields,
  onChange,
}: {
  fields: AgentOutputField[];
  onChange: (fields: AgentOutputField[]) => void;
}) {
  const set = (i: number, patch: Partial<AgentOutputField>) =>
    onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Result fields</Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        The agent fills these in through a <code>submit_result</code> tool. Steps chained under it
        can branch on a field, e.g. score ≥ 4.
      </Typography>
      {fields.map((f, i) => (
        <Stack key={i} direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
          <TextField
            label="Name"
            size="small"
            value={f.name}
            onChange={(e) => set(i, { name: e.target.value })}
            placeholder="score"
            sx={{ width: 150 }}
          />
          <TextField
            label="Type"
            select
            size="small"
            value={f.type}
            onChange={(e) =>
              set(i, { type: e.target.value as AgentOutputFieldType, enum_values: [] })
            }
            sx={{ width: 120 }}
          >
            {FIELD_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          {f.type === "enum" ? (
            <TextField
              label="Values"
              size="small"
              value={f.enum_values.join(", ")}
              onChange={(e) =>
                set(i, {
                  enum_values: e.target.value.split(",").map((v) => v.trimStart()),
                })
              }
              helperText="Comma-separated"
              sx={{ flexGrow: 1 }}
            />
          ) : (
            <TextField
              label="Description"
              size="small"
              value={f.description}
              onChange={(e) => set(i, { description: e.target.value })}
              placeholder="What the agent should put here"
              sx={{ flexGrow: 1 }}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={f.required}
                onChange={(e) => set(i, { required: e.target.checked })}
              />
            }
            label="Required"
          />
          <IconButton size="small" onClick={() => onChange(fields.filter((_, j) => j !== i))}>
            <X fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Box>
        <Button
          size="small"
          startIcon={<Plus />}
          onClick={() =>
            onChange([
              ...fields,
              { name: "", type: "string", description: "", required: true, enum_values: [] },
            ])
          }
        >
          Add field
        </Button>
      </Box>
    </Stack>
  );
}

function AgentDialog({
  draft,
  setDraft,
  tenants,
  onClose,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  tenants: Tenant[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: tools } = useQuery({ queryKey: keys.agentDefinition.tools(), queryFn: api.getAgentTools });
  const { data: contracts } = useQuery({
    queryKey: keys.agentDefinition.contracts(),
    queryFn: api.getAgentContracts,
  });
  const { data: sources } = useAgentDataSources();
  const patch = (p: Partial<Draft>) => setDraft({ ...draft, ...p });
  const isBuiltin = draft.builtin_key !== null;
  const contractLocked = isBuiltin || (draft.id !== null && draft.workflow_count > 0);

  const save = useMutation({
    mutationFn: () => {
      const body = bodyOf(draft);
      if (!draft.id) return api.createAgentDefinition(body);
      const { instructions, ...rest } = body;
      const update: AgentDefinitionUpdate = { ...rest };
      delete (update as AgentDefinitionCreate).tenant_id;
      if (draft.reset_instructions) update.reset_instructions = true;
      // A built-in agent left on its default keeps following the code default.
      else if (!(isBuiltin && draft.is_default_instructions)) update.instructions = instructions;
      return api.updateAgentDefinition(draft.id, update);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.agentDefinition.all });
      toast.success(draft.id ? "Agent saved" : "Agent created");
      onClose();
    },
  });

  const toggleTool = (name: string, on: boolean) =>
    patch({
      hive_tools: on ? [...draft.hive_tools, name] : draft.hive_tools.filter((t) => t !== name),
    });
  const toggleDataSource = (key: string, on: boolean) =>
    patch({
      data_sources: on ? [...draft.data_sources, key] : draft.data_sources.filter((k) => k !== key),
    });

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{draft.id ? `Edit agent: ${draft.name}` : "New agent"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {isBuiltin && (
            <Alert severity="info" variant="outlined">
              A built-in agent. Its output contract is fixed; its instructions can be adjusted,
              and reset to the shipped default.
            </Alert>
          )}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              size="small"
              value={draft.name}
              onChange={(e) => patch({ name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Library"
              select
              size="small"
              value={draft.tenant}
              onChange={(e) => patch({ tenant: e.target.value })}
              disabled={draft.id !== null}
              helperText={draft.id ? "Clone to copy it elsewhere" : "Global, or one tenant's copy"}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value={GLOBAL}>Global library</MenuItem>
              {tenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Description"
            size="small"
            value={draft.description}
            onChange={(e) => patch({ description: e.target.value })}
            helperText="Shown to whoever picks this agent in the workflow editor."
            fullWidth
          />
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2">Instructions</Typography>
              {isBuiltin && (draft.is_default_instructions || draft.reset_instructions) && (
                <Chip size="small" label="Default" />
              )}
              <Box sx={{ flexGrow: 1 }} />
              {isBuiltin && !draft.is_default_instructions && !draft.reset_instructions && (
                <Button
                  size="small"
                  startIcon={<History />}
                  onClick={() => patch({ reset_instructions: true })}
                >
                  Reset to default
                </Button>
              )}
            </Stack>
            <TextField
              multiline
              minRows={8}
              maxRows={24}
              value={draft.instructions}
              onChange={(e) =>
                patch({
                  instructions: e.target.value,
                  is_default_instructions: false,
                  reset_instructions: false,
                })
              }
              disabled={draft.reset_instructions}
              helperText={
                draft.reset_instructions
                  ? "Will go back to the shipped default on save."
                  : "The agent's system prompt. Each workflow step can add its own instructions on top."
              }
              sx={{ "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.85rem" } }}
              fullWidth
            />
          </Box>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Output contract"
              select
              size="small"
              value={draft.output_contract}
              onChange={(e) => patch({ output_contract: e.target.value as AgentContractKey })}
              disabled={contractLocked}
              helperText={
                contractLocked && !isBuiltin
                  ? "Workflows run this agent; its contract can't change"
                  : (contracts?.find((c) => c.key === draft.output_contract)?.description ?? "")
              }
              fullWidth
            >
              {(contracts ?? []).map((c) => (
                <MenuItem key={c.key} value={c.key}>
                  {c.label} · {c.subject_kinds.map((t) => t.replace("_", " ")).join(", ")}
                </MenuItem>
              ))}
              {!contracts && (
                <MenuItem value={draft.output_contract}>
                  {CONTRACT_LABELS[draft.output_contract]}
                </MenuItem>
              )}
            </TextField>
            <TextField
              label="Max turns"
              type="number"
              size="small"
              value={draft.max_turns}
              onChange={(e) => patch({ max_turns: Math.round(Number(e.target.value)) })}
              slotProps={{ htmlInput: { min: 1, max: 1500 } }}
              sx={{ width: 120 }}
            />
          </Stack>
          {draft.output_contract === "structured" && (
            <OutputFieldsEditor
              fields={draft.output_fields}
              onChange={(output_fields) => patch({ output_fields })}
            />
          )}
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.web_access}
                  onChange={(e) => patch({ web_access: e.target.checked })}
                />
              }
              label="Web search and fetch"
            />
            <FormControlLabel
              control={
                <Switch checked={draft.enabled} onChange={(e) => patch({ enabled: e.target.checked })} />
              }
              label="Enabled"
            />
          </Stack>
          <Box>
            <Typography variant="subtitle2">Company database tools</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              What the agent may do in the platform. Micro-app publishing and access changes are
              never available to agents.
            </Typography>
            {!tools && <CircularProgress size={20} />}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2 }}>
              {(tools ?? []).map((t) => (
                <Tooltip key={t.name} title={t.description} placement="left" enterDelay={400}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={draft.hive_tools.includes(t.name)}
                        onChange={(e) => toggleTool(t.name, e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                        {t.name}
                      </Typography>
                    }
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2">Data sources</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Where the agent's tools come from. Hive is always on; a source ticked here can be
              opted in to, with a per-run cap, on each workflow step that runs this agent. A free
              one (always on) needs no opt-in: every run of this agent gets it.
            </Typography>
            {!sources && <CircularProgress size={20} />}
            <Stack>
              {(sources ?? []).map((d) => (
                <Box key={d.key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={d.implicit || draft.data_sources.includes(d.key)}
                        disabled={d.implicit}
                        onChange={(e) => toggleDataSource(d.key, e.target.checked)}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <Typography variant="body2">
                          {d.label}
                          {d.implicit ? " · always" : ""}
                        </Typography>
                        {d.spends_money && <Chip size="small" label="Spends money" variant="outlined" />}
                        {d.always_on && <Chip size="small" label="Always on" variant="outlined" />}
                      </Stack>
                    }
                  />
                  <Typography variant="caption" sx={{ display: "block", pl: 4, color: "text.secondary" }}>
                    {d.description}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
          <AdvancedModelFold
            tenantId={draft.tenant === GLOBAL ? null : draft.tenant}
            value={draft.model}
            onChange={(model) => patch({ model })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          loading={save.isPending}
          disabled={!draft.name.trim() || (!draft.instructions.trim() && !draft.reset_instructions)}
        >
          {draft.id ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CloneDialog({
  agent,
  tenants,
  onClose,
}: {
  agent: AgentDefinition;
  tenants: Tenant[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [tenantId, setTenantId] = useState("");
  const [name, setName] = useState(agent.name);
  const clone = useMutation({
    mutationFn: () => api.cloneAgentDefinition(agent.id, { tenant_id: tenantId, name: name.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.agentDefinition.all });
      toast.success("Copied to the tenant");
      onClose();
    },
  });
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Copy to a tenant: {agent.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DialogContentText>
            The copy can be adjusted for that tenant alone. Its workflows can then pick it next to
            the global library.
          </DialogContentText>
          <TextField
            label="Tenant"
            select
            size="small"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            fullWidth
          >
            {tenants.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Name"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => clone.mutate()}
          loading={clone.isPending}
          disabled={!tenantId || !name.trim()}
        >
          Copy
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AgentDefinitionsPage() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [cloning, setCloning] = useState<AgentDefinition | null>(null);
  const [feedbackFor, setFeedbackFor] = useState<AgentDefinition | null>(null);

  const { data: agents, isLoading, error } = useQuery({
    queryKey: keys.agentDefinition.list(null),
    queryFn: () => api.getAgentDefinitions(),
  });
  const { data: tenantsData } = useTenants();
  const tenants = tenantsData ?? NO_TENANTS;
  const tenantName = useMemo(() => new Map(tenants.map((t) => [t.id, t.name])), [tenants]);

  const specs = useMemo<FilterSpec[]>(
    () => [
      {
        kind: "options",
        key: "library",
        label: "Library",
        param: "library",
        multiple: false,
        pinned: true,
        options: [
          { value: GLOBAL, label: "Global library" },
          ...tenants.map((t) => ({ value: t.id, label: `${t.name}'s copies` })),
        ],
        noneLabel: "All agents",
        searchPlaceholder: "Search tenants",
      },
    ],
    [tenants],
  );
  const filterState = useLocalFilterState();
  const filters = useFilters(specs, {
    state: filterState,
    search: { param: "q", placeholder: "Search agents" },
  });
  const library = filters.get("library");
  const query = (filters.get("q") ?? "").trim().toLowerCase();
  const { data: sourcesData } = useAgentDataSources();
  const sources = sourcesData ?? NO_SOURCES;
  const sourceLabel = (key: string) => sources.find((d) => d.key === key)?.label ?? key;
  // Implicit sources (Hive) first, then the agent's own in catalog order.
  const sourcesOf = (a: AgentDefinition) =>
    [
      ...sources.filter((d) => d.implicit).map((d) => d.label),
      ...(a.data_sources ?? []).map(sourceLabel),
    ].join(", ") || "Hive";

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteAgentDefinition(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.agentDefinition.all });
      toast.success("Agent deleted");
    },
  });

  const shown = (agents ?? []).filter(
    (a) =>
      (!library || (library === GLOBAL ? a.tenant_id === null : a.tenant_id === library)) &&
      (!query || a.name.toLowerCase().includes(query)),
  );
  const total = agents?.length ?? 0;
  const agentsWord = total === 1 ? "agent" : "agents";

  return (
    <>
      <PageHeader
        title="Agents"
        description={
          <>
            The agents workflows run in their agent step. An agent is its instructions, model and
            tools, plus an output contract: what it hands back, and so what the steps after it can
            branch on. Runs bill the tenant's organization's Claude plan.
          </>
        }
        actions={
          <Button
            variant="contained"
            startIcon={<Plus />}
            onClick={() => setDraft(newDraft(library ?? GLOBAL))}
          >
            New agent
          </Button>
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error)}
        </Alert>
      )}
      {(isLoading || agents) && (
        <TableCard
          loading={isLoading}
          toolbar={
            <FilterBar
              filters={filters}
              count={
                agents &&
                (filters.activeCount > 0
                  ? `${formatNumber(shown.length)} of ${formatNumber(total)} ${agentsWord}`
                  : `${formatNumber(total)} ${agentsWord}`)
              }
            />
          }
        >
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Library</TableCell>
                <TableCell>Output</TableCell>
                <TableCell>Tools</TableCell>
                <TableCell>Sources</TableCell>
                <TableCell align="right">Workflows</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((a) => (
                <TableRow
                  key={a.id}
                  hover
                  sx={{ cursor: "pointer", opacity: a.enabled ? 1 : 0.6 }}
                  onClick={() => setDraft(draftOf(a))}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {a.name}
                      </Typography>
                      {a.builtin_key && <Chip size="small" label="Built-in" />}
                      {!a.enabled && <Chip size="small" label="Disabled" />}
                    </Stack>
                    {a.description && (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {a.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <ScopeChip
                      tenantId={a.tenant_id}
                      tenantName={
                        a.tenant_id ? (tenantName.get(a.tenant_id) ?? "Tenant copy") : undefined
                      }
                    />
                  </TableCell>
                  <TableCell>{CONTRACT_LABELS[a.output_contract]}</TableCell>
                  <TableCell>
                    {a.hive_tools.length}
                    {a.web_access ? " + web" : ""}
                  </TableCell>
                  <TableCell>{sourcesOf(a)}</TableCell>
                  <TableCell align="right">{formatNumber(a.workflow_count)}</TableCell>
                  <TableCell padding="checkbox" align="right">
                    <RowActions
                      label={a.name}
                      primary={{
                        label: "Edit",
                        icon: <Pencil fontSize="small" />,
                        onClick: () => setDraft(draftOf(a)),
                      }}
                      actions={[
                        {
                          label: "Reviewer feedback",
                          icon: <MessageSquareText fontSize="small" />,
                          onClick: () => setFeedbackFor(a),
                        },
                        {
                          label: "Copy to a tenant",
                          icon: <Copy fontSize="small" />,
                          onClick: () => setCloning(a),
                        },
                        {
                          label: a.workflow_count ? "Delete (used by workflows)" : "Delete",
                          icon: <Trash2 fontSize="small" />,
                          destructive: true,
                          hidden: !!a.builtin_key,
                          disabled: a.workflow_count > 0,
                          onClick: () => remove.mutateAsync(a.id),
                          confirm: {
                            title: `Delete agent: ${a.name}`,
                            body: "Runs it already made keep their history.",
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && (
                <EmptyTableRow colSpan={7}>
                  {total === 0
                    ? "No agents yet"
                    : query
                      ? "No agents match your filters"
                      : "No agents in this library yet"}
                </EmptyTableRow>
              )}
            </TableBody>
          </ScrollTable>
        </TableCard>
      )}
      {draft && (
        <AgentDialog draft={draft} setDraft={setDraft} tenants={tenants} onClose={() => setDraft(null)} />
      )}
      {cloning && <CloneDialog agent={cloning} tenants={tenants} onClose={() => setCloning(null)} />}
      {feedbackFor && (
        <AgentFeedbackDialog
          agent={feedbackFor}
          tenants={tenants}
          onClose={() => setFeedbackFor(null)}
        />
      )}
    </>
  );
}
